/**
 * Development-only logger utility
 * Logs are only output in development mode to prevent sensitive data leakage in production
 */

const isDevelopment = import.meta.env.DEV;

type LogLevel = 'log' | 'info' | 'warn' | 'error' | 'debug';

interface LoggerOptions {
  /** Force logging even in production (use sparingly, only for critical errors) */
  forceInProduction?: boolean;
  /** Sanitize the data before logging (remove sensitive fields) */
  sanitize?: boolean;
}

// Fields that should never be logged
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'access_token',
  'refresh_token',
  'api_key',
  'apiKey',
  'secret',
  'credit_card',
  'creditCard',
  'ssn',
  'social_security',
];

/**
 * Sanitize an object by removing sensitive fields
 */
function sanitizeData(data: unknown): unknown {
  if (data === null || data === undefined) {
    return data;
  }

  if (typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Create a log function for a specific level
 */
function createLogger(level: LogLevel) {
  return (message: string, data?: unknown, options?: LoggerOptions) => {
    // Only log in development unless forced
    if (!isDevelopment && !options?.forceInProduction) {
      return;
    }

    const logData = options?.sanitize && data ? sanitizeData(data) : data;

    if (data !== undefined) {
      console[level](`[${level.toUpperCase()}]`, message, logData);
    } else {
      console[level](`[${level.toUpperCase()}]`, message);
    }
  };
}

/**
 * Development-only logger
 * All logs are automatically suppressed in production builds
 */
export const logger = {
  /** General log (development only) */
  log: createLogger('log'),
  /** Info level (development only) */
  info: createLogger('info'),
  /** Warning level (development only) */
  warn: createLogger('warn'),
  /** Error level - use forceInProduction: true for critical errors that need logging */
  error: createLogger('error'),
  /** Debug level (development only) */
  debug: createLogger('debug'),
};

export default logger;
